import { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Briefcase,
  History,
  DollarSign,
  ArrowUp,
  ArrowDown,
  Minus,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { SalaryBreakdown } from "../components/SalaryBreakdown";
import { toast } from "sonner";
import api from "../lib/api";

const salaryTypeLabels = { monthly: "Mensual", daily: "Diario" };

const emptyForm = {
  name: "",
  ccss_code: "",
  ins_code: "",
  acronym: "",
  base_salary: 0,
  salary_type: "monthly",
  notes: "",
};

const emptySalaryForm = {
  salary_amount: 0,
  salary_type: "",
  effective_date: "",
  legal_reference: "",
  notes: "",
};

export default function Occupations({ embedded = false }) {
  const [occupations, setOccupations] = useState([]);
  const [cfg, setCfg] = useState({ overtime_multiplier: 1.5 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Salary update dialog
  const [salaryDialogOpen, setSalaryDialogOpen] = useState(false);
  const [salaryTarget, setSalaryTarget] = useState(null);
  const [salaryForm, setSalaryForm] = useState(emptySalaryForm);

  // History dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyData, setHistoryData] = useState([]);

  // Propagate salary confirmation
  const [propagate, setPropagate] = useState({ open: false });

  const maybeOfferPropagation = async (occId, salary, salaryType) => {
    try {
      const { data } = await api.get(`/occupations/${occId}/active-employees-count`);
      if ((data.active_employees || 0) > 0) {
        setPropagate({ open: true, occId, count: data.active_employees, salary, salaryType });
      }
    } catch {
      /* silencioso: la propagación es opcional */
    }
  };

  const confirmPropagate = async () => {
    try {
      const { data } = await api.post(`/occupations/${propagate.occId}/propagate-salary`);
      toast.success(`Salario actualizado en ${data.updated} trabajador(es)`);
    } catch {
      toast.error("Error al propagar el salario");
    }
    setPropagate({ open: false });
  };

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/occupations");
      setOccupations(data);
    } catch {
      toast.error("Error al cargar ocupaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    api.get("/payroll-config").then(({ data }) => setCfg(data || {})).catch(() => {});
  }, [fetchData]);

  // CRUD handlers
  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (o) => {
    setEditId(o.id);
    setForm({
      name: o.name,
      ccss_code: o.ccss_code || "",
      ins_code: o.ins_code || "",
      acronym: o.acronym || "",
      base_salary: o.base_salary || 0,
      salary_type: o.salary_type || "monthly",
      notes: o.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.base_salary) {
      toast.error("Nombre y salario minimo son requeridos");
      return;
    }
    try {
      const payload = { ...form, base_salary: Number(form.base_salary) || 0 };
      if (editId) {
        const original = occupations.find((o) => o.id === editId);
        await api.put(`/occupations/${editId}`, payload);
        toast.success("Ocupacion actualizada");
        setDialogOpen(false);
        await fetchData();
        const salaryChanged =
          original &&
          (Number(original.base_salary) !== Number(payload.base_salary) ||
            (original.salary_type || "monthly") !== payload.salary_type);
        if (salaryChanged) {
          await maybeOfferPropagation(editId, payload.base_salary, payload.salary_type);
        }
        return;
      } else {
        await api.post("/occupations", payload);
        toast.success("Ocupacion creada");
      }
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Error al guardar ocupacion");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar esta ocupacion y todo su historial?")) return;
    try {
      await api.delete(`/occupations/${id}`);
      toast.success("Ocupacion eliminada");
      fetchData();
    } catch {
      toast.error("Error al eliminar ocupacion");
    }
  };

  // Salary update
  const openSalaryDialog = (occ) => {
    setSalaryTarget(occ);
    setSalaryForm({
      salary_amount: occ.base_salary || 0,
      salary_type: occ.salary_type || "monthly",
      effective_date: new Date().toISOString().slice(0, 10),
      legal_reference: "",
      notes: "",
    });
    setSalaryDialogOpen(true);
  };

  const handleSalaryUpdate = async () => {
    if (!salaryForm.salary_amount || !salaryForm.effective_date) {
      toast.error("Monto y fecha de vigencia son requeridos");
      return;
    }
    try {
      await api.put(`/occupations/${salaryTarget.id}/salary`, {
        ...salaryForm,
        salary_amount: Number(salaryForm.salary_amount),
      });
      toast.success("Salario actualizado");
      setSalaryDialogOpen(false);
      await fetchData();
      await maybeOfferPropagation(
        salaryTarget.id,
        Number(salaryForm.salary_amount),
        salaryForm.salary_type
      );
    } catch {
      toast.error("Error al actualizar salario");
    }
  };

  // History
  const openHistory = async (occ) => {
    setHistoryTarget(occ);
    setHistoryDialogOpen(true);
    try {
      const { data } = await api.get(`/occupations/${occ.id}/salary-history`);
      setHistoryData(data);
    } catch {
      toast.error("Error al cargar historial");
      setHistoryData([]);
    }
  };

  const formatCurrency = (val) =>
    val ? `\u20A1${Number(val).toLocaleString("es-CR")}` : "\u2014";

  return (
    <div data-testid="occupations-page">
      <div className="flex items-center justify-between mb-6">
        {!embedded && (
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
              Ocupaciones
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              Ocupaciones y escalas salariales
            </p>
          </div>
        )}
        {embedded && <div />}
        <Button
          onClick={openCreate}
          className="bg-zinc-900 text-white hover:bg-zinc-800"
          data-testid="add-occupation-button"
        >
          <Plus className="w-4 h-4 mr-2" /> Nueva ocupacion
        </Button>
      </div>

      <Card className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-100 hover:bg-transparent">
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Ocupacion
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden sm:table-cell">
                  Siglas
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  CCSS
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide hidden md:table-cell">
                  INS
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Salario minimo
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide">
                  Fijacion
                </TableHead>
                <TableHead className="font-medium text-zinc-900 text-xs uppercase tracking-wide text-right">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {occupations.map((o) => (
                <TableRow
                  key={o.id}
                  className="border-zinc-100"
                  data-testid={`occupation-row-${o.id}`}
                >
                  <TableCell className="text-sm text-zinc-900 font-medium">
                    {o.name}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 font-mono hidden sm:table-cell">
                    {o.acronym || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 font-mono hidden md:table-cell">
                    {o.ccss_code || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-500 font-mono hidden md:table-cell">
                    {o.ins_code || "\u2014"}
                  </TableCell>
                  <TableCell className="text-sm text-zinc-900 font-semibold tabular-nums">
                    {formatCurrency(o.base_salary)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="font-medium bg-zinc-50 text-zinc-700 border-zinc-200"
                    >
                      {salaryTypeLabels[o.salary_type] || o.salary_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openSalaryDialog(o)}
                      title="Modificar salario"
                      data-testid={`update-salary-${o.id}`}
                    >
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openHistory(o)}
                      title="Historial de cambios"
                      data-testid={`view-history-${o.id}`}
                    >
                      <History className="w-4 h-4 text-blue-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(o)}
                      data-testid={`edit-occupation-${o.id}`}
                    >
                      <Pencil className="w-4 h-4 text-zinc-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(o.id)}
                      data-testid={`delete-occupation-${o.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {occupations.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-16 text-zinc-400"
                  >
                    <Briefcase className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No hay ocupaciones registradas</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / Edit Occupation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              {editId ? "Editar ocupacion" : "Nueva ocupacion"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-xs font-medium text-zinc-700">
                Nombre de ocupacion *
              </Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Operario general"
                data-testid="occupation-name-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Codigo CCSS
                </Label>
                <Input
                  value={form.ccss_code}
                  onChange={(e) =>
                    setForm({ ...form, ccss_code: e.target.value })
                  }
                  placeholder="9211"
                  data-testid="occupation-ccss-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Codigo INS
                </Label>
                <Input
                  value={form.ins_code}
                  onChange={(e) =>
                    setForm({ ...form, ins_code: e.target.value })
                  }
                  placeholder="9211"
                  data-testid="occupation-ins-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Siglas
                </Label>
                <Input
                  value={form.acronym}
                  onChange={(e) =>
                    setForm({ ...form, acronym: e.target.value })
                  }
                  placeholder="OPG"
                  data-testid="occupation-acronym-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  {form.salary_type === "daily" ? "Salario diario *" : "Salario mensual *"}
                </Label>
                <Input
                  type="number"
                  value={form.base_salary}
                  onChange={(e) =>
                    setForm({ ...form, base_salary: e.target.value })
                  }
                  placeholder="350000"
                  data-testid="occupation-salary-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Fijacion salarial *
                </Label>
                <Select
                  value={form.salary_type}
                  onValueChange={(v) =>
                    setForm({ ...form, salary_type: v })
                  }
                >
                  <SelectTrigger data-testid="occupation-salary-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensual</SelectItem>
                    <SelectItem value="daily">Diaria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {Number(form.base_salary) > 0 && (
              <SalaryBreakdown
                amount={form.base_salary}
                salaryType={form.salary_type}
                cfg={cfg}
                testid="occupation-salary-breakdown"
              />
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="occupation-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="occupation-submit-button"
            >
              {editId ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary Update Dialog */}
      <Dialog open={salaryDialogOpen} onOpenChange={setSalaryDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Modificar salario
            </DialogTitle>
          </DialogHeader>
          {salaryTarget && (
            <div className="space-y-4 py-4">
              <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-100">
                <p className="text-sm font-medium text-zinc-900">
                  {salaryTarget.name}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Salario actual:{" "}
                  <span className="font-semibold text-zinc-700">
                    {formatCurrency(salaryTarget.base_salary)}
                  </span>
                  {" "}({salaryTypeLabels[salaryTarget.salary_type]})
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-zinc-700">
                    Nuevo salario *
                  </Label>
                  <Input
                    type="number"
                    value={salaryForm.salary_amount}
                    onChange={(e) =>
                      setSalaryForm({
                        ...salaryForm,
                        salary_amount: e.target.value,
                      })
                    }
                    data-testid="salary-amount-input"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-700">
                    Fijacion
                  </Label>
                  <Select
                    value={salaryForm.salary_type}
                    onValueChange={(v) =>
                      setSalaryForm({ ...salaryForm, salary_type: v })
                    }
                  >
                    <SelectTrigger data-testid="salary-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensual</SelectItem>
                      <SelectItem value="daily">Diario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {Number(salaryForm.salary_amount) > 0 && (
                <SalaryBreakdown
                  amount={salaryForm.salary_amount}
                  salaryType={salaryForm.salary_type}
                  cfg={cfg}
                  testid="salary-dialog-breakdown"
                />
              )}
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Fecha de vigencia *
                </Label>
                <Input
                  type="date"
                  value={salaryForm.effective_date}
                  onChange={(e) =>
                    setSalaryForm({
                      ...salaryForm,
                      effective_date: e.target.value,
                    })
                  }
                  data-testid="salary-effective-date-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Referencia legal
                </Label>
                <Input
                  value={salaryForm.legal_reference}
                  onChange={(e) =>
                    setSalaryForm({
                      ...salaryForm,
                      legal_reference: e.target.value,
                    })
                  }
                  placeholder="Decreto N.o XXXX, Gaceta..."
                  data-testid="salary-legal-ref-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-zinc-700">
                  Notas
                </Label>
                <Input
                  value={salaryForm.notes}
                  onChange={(e) =>
                    setSalaryForm({ ...salaryForm, notes: e.target.value })
                  }
                  placeholder="Ajuste anual 2026"
                  data-testid="salary-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSalaryDialogOpen(false)}
              data-testid="salary-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSalaryUpdate}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="salary-submit-button"
            >
              Actualizar salario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Salary History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Historial salarial
            </DialogTitle>
          </DialogHeader>
          {historyTarget && (
            <div>
              <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-100 mb-4">
                <p className="text-sm font-medium text-zinc-900">
                  {historyTarget.name}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Salario vigente:{" "}
                  <span className="font-semibold text-zinc-700">
                    {formatCurrency(historyTarget.base_salary)}
                  </span>
                </p>
              </div>
              {historyData.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-8">
                  No hay historial de cambios
                </p>
              ) : (
                <div className="space-y-0">
                  {historyData.map((h, i) => {
                    const isIncrease =
                      h.previous_salary != null &&
                      h.salary_amount > h.previous_salary;
                    const isDecrease =
                      h.previous_salary != null &&
                      h.salary_amount < h.previous_salary;
                    return (
                      <div
                        key={h.id || i}
                        className="flex gap-3 items-start py-3 border-b border-zinc-100 last:border-0"
                      >
                        <div
                          className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                            isIncrease
                              ? "bg-emerald-50"
                              : isDecrease
                                ? "bg-red-50"
                                : "bg-zinc-100"
                          }`}
                        >
                          {isIncrease ? (
                            <ArrowUp className="w-3.5 h-3.5 text-emerald-600" />
                          ) : isDecrease ? (
                            <ArrowDown className="w-3.5 h-3.5 text-red-600" />
                          ) : (
                            <Minus className="w-3.5 h-3.5 text-zinc-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                              {formatCurrency(h.salary_amount)}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-[10px] font-medium"
                            >
                              {salaryTypeLabels[h.salary_type] || h.salary_type}
                            </Badge>
                            {h.previous_salary != null && (
                              <span className="text-xs text-zinc-400">
                                (anterior: {formatCurrency(h.previous_salary)})
                              </span>
                            )}
                          </div>
                          {h.legal_reference && (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              {h.legal_reference}
                            </p>
                          )}
                          {h.notes && (
                            <p className="text-xs text-zinc-400 mt-0.5">
                              {h.notes}
                            </p>
                          )}
                          <p className="text-[11px] text-zinc-400 mt-1">
                            Vigente desde: {h.effective_date}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHistoryDialogOpen(false)}
              data-testid="history-close-button"
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Propagate Salary Confirmation */}
      <Dialog
        open={propagate.open}
        onOpenChange={(o) => setPropagate((p) => ({ ...p, open: o }))}
      >
        <DialogContent className="sm:max-w-md" data-testid="propagate-salary-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-950">
              Actualizar salario de los trabajadores
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-zinc-600">
              Esta ocupación tiene{" "}
              <span className="font-semibold text-zinc-900" data-testid="propagate-count">
                {propagate.count} trabajador{propagate.count === 1 ? "" : "es"} activo
                {propagate.count === 1 ? "" : "s"}
              </span>
              . ¿Desea actualizar su salario al nuevo monto{" "}
              <span className="font-semibold text-zinc-900">
                {formatCurrency(propagate.salary)}
              </span>{" "}
              ({salaryTypeLabels[propagate.salaryType] || propagate.salaryType})?
            </p>
            <p className="text-xs text-zinc-400">
              Si elige "Solo la ocupación", el salario de los trabajadores actuales no
              cambiará; solo aplicará a los nuevos.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPropagate({ open: false })}
              data-testid="propagate-skip-button"
            >
              Solo la ocupación
            </Button>
            <Button
              onClick={confirmPropagate}
              className="bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="propagate-confirm-button"
            >
              Sí, actualizar {propagate.count} trabajador{propagate.count === 1 ? "" : "es"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
